/**
 * RentalCartPage — multi-seller rental cart + checkout.
 *
 * Buyers can shortlist rental items from many ateliers (via the
 * RentalsPage drawer's "Add to cart" button or the inline "+ Add"
 * button on search results) and check out everything in one go.
 *
 * Endpoints:
 *   GET    /api/rentals/cart                — grouped list + totals
 *   DELETE /api/rentals/cart/{cart_item_id} — remove a single line
 *   POST   /api/rentals/cart/clear          — empty the cart
 *   POST   /api/rentals/cart/checkout       — reserves all groups
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, MapPin, Package, ChevronLeft, Calendar,
         CreditCard, Trash2, Store } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { fmtMoney } from "../lib/money";
import { useAuth } from "../lib/AuthContext";


export default function RentalCartPage() {
  const { user } = useAuth();
  const [cart, setCart] = useState({ groups: [], grand_total: 0, item_count: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null); // { success_count, failure_count, reservations, failed }
  // Shared address for the whole checkout (all groups).
  const [addr, setAddr] = useState({ line1: "", city: "", pincode: "", phone: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rentals/cart");
      setCart(data || { groups: [], grand_total: 0, item_count: 0 });
    } catch { setCart({ groups: [], grand_total: 0, item_count: 0 }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (cartItemId) => {
    try { await api.delete(`/rentals/cart/${cartItemId}`); load(); }
    catch { setErr("Couldn't remove item"); }
  };

  const clearAll = async () => {
    if (!cart.item_count) return;
    if (!confirm("Empty the rental cart?")) return;
    try { await api.post("/rentals/cart/clear"); load(); } catch { /* swallow */ }
  };

  const checkout = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/rentals/cart/checkout", {
        address: addr.line1 ? addr : undefined,
      });
      setResult(data);
      load();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Checkout failed");
    } finally { setBusy(false); }
  };

  if (!user) return null;

  // Result splash screen — after a successful (or partial) checkout.
  if (result) {
    return (
      <div className="min-h-screen px-4 sm:px-6 py-10" data-testid="rental-checkout-result">
        <div className="max-w-2xl mx-auto card p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#D4FF00] text-black flex items-center justify-center mb-4">
            <ShoppingBag size={28}/>
          </div>
          <h1 className="font-display font-black text-3xl uppercase mb-2">Reservations placed</h1>
          <p className="text-sm text-[#A1A1AA] mb-6">
            <span className="text-[#D4FF00] font-bold">{result.success_count}</span> confirmed
            {result.failure_count > 0 && (
              <> · <span className="text-[#FF007F] font-bold">{result.failure_count}</span> failed</>
            )}
          </p>
          {result.failed?.length > 0 && (
            <div className="text-left text-xs space-y-2 mb-6">
              {result.failed.map((f, i) => (
                <div key={`${f.store_id}-${i}`} className="bg-[#FF007F]/10 border border-[#FF007F]/30 rounded p-2"
                  data-testid={`rental-checkout-fail-${i}`}>
                  <div className="text-[#FF007F] font-bold">Store {f.store_id}</div>
                  <div className="text-[#A1A1AA]">{f.reason}</div>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 justify-center">
            <Link to="/my-rentals" className="btn-primary"
              data-testid="rental-checkout-my-rentals">View my rentals</Link>
            <Link to="/rentals" className="btn-secondary"
              data-testid="rental-checkout-keep-shopping">Keep browsing</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6 pb-24" data-testid="rental-cart-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <Link to="/rentals" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] hover:text-[#D4FF00] mb-4"
          data-testid="rental-cart-back">
          <ChevronLeft size={11}/> Back to rentals
        </Link>
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase leading-none">
              Your rental<br/>
              <span className="text-[#D4FF00]">cart</span>
            </h1>
            <p className="text-sm text-[#A1A1AA] mt-2">
              Shortlist items from any number of ateliers, then check them all out at once.
            </p>
          </div>
          {cart.item_count > 0 && (
            <button onClick={clearAll}
              data-testid="rental-cart-clear"
              className="btn-secondary text-xs inline-flex items-center gap-1">
              <Trash2 size={11}/> Clear cart
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#A1A1AA] text-sm">Loading cart…</div>
        ) : cart.item_count === 0 ? (
          <div className="card p-12 text-center" data-testid="rental-cart-empty">
            <Package size={40} className="mx-auto text-[#A1A1AA] opacity-40 mb-3"/>
            <div className="font-display font-bold text-xl uppercase mb-1">Cart is empty</div>
            <div className="text-sm text-[#A1A1AA] mb-6">Find designer pieces, jewellery, sneakers and more on rent.</div>
            <Link to="/rentals" className="btn-primary inline-flex"
              data-testid="rental-cart-shop">Browse rentals</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_320px] gap-6">
            {/* Groups */}
            <div className="space-y-4">
              {cart.groups.map(g => (
                <div key={g.store_id} className="card p-4"
                  data-testid={`rental-cart-group-${g.store_id}`}>
                  <div className="flex items-center gap-3 pb-3 border-b border-[#3D2845] mb-3">
                    {g.cover
                      ? <img src={g.cover} className="w-10 h-10 rounded object-cover" alt=""/>
                      : <div className="w-10 h-10 rounded bg-[#3D2845] flex items-center justify-center"><Store size={14}/></div>}
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold uppercase text-sm truncate">{g.store_name}</div>
                      <div className="text-[10px] text-[#A1A1AA] flex items-center gap-1">
                        <MapPin size={9}/>{g.city} · {g.items.length} item{g.items.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[9px] text-[#A1A1AA] uppercase tracking-widest">Subtotal</div>
                      <div className="font-display font-black text-[#D4FF00] text-lg"
                        data-testid={`rental-cart-group-total-${g.store_id}`}>{fmtMoney(g.store_total)}</div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {g.items.map(it => (
                      <div key={it.cart_item_id} className="flex items-center gap-3"
                        data-testid={`rental-cart-item-${it.cart_item_id}`}>
                        <img src={it.image} className="w-14 h-14 rounded object-cover" alt=""/>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm truncate">{it.name}</div>
                          <div className="text-[10px] text-[#A1A1AA]">
                            {fmtMoney(it.price_per_day)}/day × {it.days} days · Dep {fmtMoney(it.deposit, { decimals: 0 })}
                            {it.size && <> · Size <span className="text-[#D4FF00]">{it.size}</span></>}
                            {it.color && (
                              <> · <span className="inline-flex items-center gap-1 align-middle">
                                <span className="w-2.5 h-2.5 rounded-full border border-[#3D2845]"
                                  style={{ background: it.color_hex || "#888" }}/>
                                <span className="text-[#FAFAFA]">{it.color}</span>
                              </span></>
                            )}
                            {it.qty > 1 && <> · ×{it.qty}</>}
                          </div>
                          <div className="text-[10px] text-[#A1A1AA] flex items-center gap-1 mt-0.5">
                            <Calendar size={9}/>{it.start_date} → {it.end_date}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-bold text-sm">{fmtMoney(it.line_total)}</div>
                          <div className="text-[9px] text-[#00F0FF]">+{fmtMoney(it.line_deposit, { decimals: 0 })} dep</div>
                          <button onClick={() => remove(it.cart_item_id)}
                            data-testid={`rental-cart-remove-${it.cart_item_id}`}
                            className="text-[#FF007F] text-xs mt-1 hover:underline">Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Summary panel */}
            <aside className="lg:sticky lg:top-4 lg:self-start space-y-3">
              <div className="card p-4">
                <div className="font-display font-bold uppercase text-sm mb-3">Delivery address</div>
                <div className="space-y-2">
                  <input value={addr.line1} onChange={e => setAddr({ ...addr, line1: e.target.value })}
                    placeholder="Street address" className="input-dark text-xs w-full"
                    data-testid="rental-checkout-addr-line1"/>
                  <div className="grid grid-cols-2 gap-2">
                    <input value={addr.city} onChange={e => setAddr({ ...addr, city: e.target.value })}
                      placeholder="City" className="input-dark text-xs"
                      data-testid="rental-checkout-addr-city"/>
                    <input value={addr.pincode} onChange={e => setAddr({ ...addr, pincode: e.target.value })}
                      placeholder="PIN" className="input-dark text-xs"
                      data-testid="rental-checkout-addr-pincode"/>
                  </div>
                  <input value={addr.phone} onChange={e => setAddr({ ...addr, phone: e.target.value })}
                    placeholder="Phone" className="input-dark text-xs w-full"
                    data-testid="rental-checkout-addr-phone"/>
                </div>
              </div>

              <div className="card p-4">
                <div className="space-y-2 text-sm">
                  {cart.groups.map(g => (
                    <div key={g.store_id} className="flex justify-between text-xs">
                      <span className="text-[#A1A1AA] truncate pr-2">{g.store_name}</span>
                      <span className="font-bold">{fmtMoney(g.store_total)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-[#3D2845] mt-3 pt-3 flex justify-between items-baseline">
                  <span className="font-bold text-sm">Grand total</span>
                  <span className="font-display font-black text-[#D4FF00] text-2xl"
                    data-testid="rental-cart-grand-total">{fmtMoney(cart.grand_total)}</span>
                </div>
                <div className="text-[9px] text-[#A1A1AA] text-right mt-1">
                  Includes refundable deposits
                </div>
                {err && (
                  <div className="mt-3 text-[10px] text-[#FF007F] bg-[#FF007F]/10 border border-[#FF007F]/30 rounded p-2"
                    data-testid="rental-cart-error">{err}</div>
                )}
                <button onClick={checkout} disabled={busy}
                  className="btn-primary w-full mt-4 inline-flex items-center justify-center gap-2"
                  data-testid="rental-cart-checkout-btn">
                  <CreditCard size={14}/>
                  {busy ? "Reserving across stores…" : `Reserve all (${cart.item_count})`}
                </button>
                <div className="text-[9px] text-[#A1A1AA] text-center mt-2">
                  Each store creates a separate reservation. Partial success is allowed.
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
